import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Separator } from '../../components/ui/separator';
import { Switch } from '../../components/ui/switch';
import { 
  Bell,
  Mail,
  FileText,
  Users,
  Receipt,
  Save,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const SettingsNotifications = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  
  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    projectUpdates: true,
    projectStatusChanges: true,
    invoiceReminders: true,
    invoicePayments: true,
    weeklyDigest: false,
    newClientAlerts: true,
    teamMemberActivity: false,
    billingAlerts: true
  });

  const handleSave = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      toast.success('Notification preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setLoading(false);
    }
  };

  const NotificationToggle = ({ id, title, description, checked, disabled }) => (
    <div className="flex items-center justify-between py-3">
      <div>
        <p className="font-medium text-slate-900">{title}</p>
        <p className="text-sm text-slate-500">{description}</p>
      </div>
      <Switch 
        checked={checked}
        onCheckedChange={(value) => setNotifications({ ...notifications, [id]: value })}
        disabled={disabled || !notifications.emailNotifications}
        data-testid={`${id}-switch`}
      />
    </div>
  );

  return (
    <div className="space-y-6 max-w-4xl animate-fade-in" data-testid="settings-notifications-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
          Notification Settings
        </h1>
        <p className="text-slate-500 mt-1">Choose how and when you receive notifications</p>
      </div>

      {/* Master Switch */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
                <Mail size={24} className="text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Email Notifications</p>
                <p className="text-sm text-slate-500">Master switch for all email notifications</p>
              </div>
            </div>
            <Switch 
              checked={notifications.emailNotifications}
              onCheckedChange={(checked) => setNotifications({ ...notifications, emailNotifications: checked })}
              data-testid="email-notifications-master-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Project Notifications */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <FileText size={20} className="text-slate-500" />
            Project Notifications
          </CardTitle>
          <CardDescription>Stay updated on project activity</CardDescription>
        </CardHeader>
        <CardContent className="p-6 divide-y divide-slate-100">
          <NotificationToggle
            id="projectUpdates"
            title="Project Updates"
            description="Get notified when projects are created or modified"
            checked={notifications.projectUpdates}
          />
          <NotificationToggle
            id="projectStatusChanges"
            title="Status Changes"
            description="Get notified when project status changes"
            checked={notifications.projectStatusChanges}
          />
        </CardContent>
      </Card>

      {/* Invoice & Billing Notifications */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Receipt size={20} className="text-slate-500" />
            Invoice & Billing
          </CardTitle>
          <CardDescription>Notifications about invoices and payments</CardDescription>
        </CardHeader>
        <CardContent className="p-6 divide-y divide-slate-100">
          <NotificationToggle
            id="invoiceReminders"
            title="Invoice Reminders"
            description="Receive reminders for pending and overdue invoices"
            checked={notifications.invoiceReminders}
          />
          <NotificationToggle
            id="invoicePayments"
            title="Payment Received"
            description="Get notified when invoices are marked as paid"
            checked={notifications.invoicePayments}
          />
          <NotificationToggle
            id="billingAlerts"
            title="Billing Alerts"
            description="Get alerts for recurring billing renewals"
            checked={notifications.billingAlerts}
          />
        </CardContent>
      </Card>

      {/* Client & Team Notifications */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Users size={20} className="text-slate-500" />
            Clients & Team
          </CardTitle>
          <CardDescription>Notifications about clients and team activity</CardDescription>
        </CardHeader>
        <CardContent className="p-6 divide-y divide-slate-100">
          <NotificationToggle
            id="newClientAlerts"
            title="New Client Added"
            description="Get notified when new clients are added"
            checked={notifications.newClientAlerts}
          />
          <NotificationToggle
            id="teamMemberActivity"
            title="Team Member Activity"
            description="Get notified about team member actions"
            checked={notifications.teamMemberActivity}
          />
        </CardContent>
      </Card>

      {/* Digest */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Bell size={20} className="text-slate-500" />
            Summary & Digest
          </CardTitle>
          <CardDescription>Periodic summary notifications</CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <NotificationToggle
            id="weeklyDigest"
            title="Weekly Digest"
            description="Receive a weekly summary of all activity every Monday"
            checked={notifications.weeklyDigest}
          />
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave}
          disabled={loading}
          className="bg-slate-900 hover:bg-slate-800"
          data-testid="save-notifications-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Preferences
        </Button>
      </div>
    </div>
  );
};

export default SettingsNotifications;
